import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy, ShieldAlert, FlaskConical, Sparkles, X, ClipboardList, AlertTriangle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { INCOME_CATEGORIES, fmtDate, fmtEUR, todayISO } from "../lib/format";
import { Money } from "../context/PrivacyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MoneyMovementFields from "../components/MoneyMovementFields";
import ConceptSelect from "../components/ConceptSelect";
import MemberSelect, { MemberBadge } from "../components/MemberSelect";
import MovementTrace from "../components/MovementTrace";

const TYPES = [["unico", "Único"], ["recurrente", "Recurrente"], ["estacional", "Estacional"], ["programado", "Programado"]];
const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const EXP_OPTIONS = [["12", "12 h"], ["24", "24 h"], ["48", "48 h"]];

const EMPTY = { date: todayISO(), concept: "", concept_id: "", member_id: null, amount: "", category: "", type: "unico", schedule: [], recurring: false, account_id: "", payment_method_id: "", is_simulated: false, expires_at: null };

function IncomeForm({ form, setForm, onSubmit, submitLabel, testPrefix, simulateMode }) {
  const needsSchedule = form.type === "estacional" || form.type === "programado";
  const disabled = !form.concept || (!needsSchedule && (!form.amount || parseFloat(String(form.amount).replace(",", ".")) <= 0)) || (needsSchedule && form.schedule.length === 0);
  return (
    <div className="space-y-3">
      <Input data-testid={`${testPrefix}-concept`} placeholder="Concepto" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Tipo</label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, recurring: v === "recurrente" })}>
            <SelectTrigger data-testid={`${testPrefix}-type`}><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Concepto (Catálogo Maestro)</label>
          <ConceptSelect
            kind="ingreso"
            value={form.concept_id}
            legacyLabel={!form.concept_id ? form.category : ""}
            onChange={(id, obj) => setForm({ ...form, concept_id: id || "", concept: form.concept || obj?.name || "" })}
            testId={`${testPrefix}-concept-select`}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Pertenece a</label>
          <MemberSelect value={form.member_id} onChange={(id) => setForm({ ...form, member_id: id })} preselectCurrent={testPrefix === "new"} testId={`${testPrefix}-member`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Fecha (referencia)</label>
          <Input data-testid={`${testPrefix}-date`} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        {!needsSchedule && (
          <div>
            <label className="text-xs text-muted-foreground">Importe (€) *</label>
            <Input data-testid={`${testPrefix}-amount`} inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="text-lg font-semibold tabular" />
          </div>
        )}
      </div>
      {needsSchedule && (
        <div className="border border-border rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Calendario de cobros</span>
            <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-add-schedule`}
              onClick={() => setForm({ ...form, schedule: [...form.schedule, { month: 1, day: 1, amount: "", label: "" }] })}>
              <Plus size={13} className="mr-1" /> Añadir
            </Button>
          </div>
          {form.schedule.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">Sin cobros programados</div>
          ) : form.schedule.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_60px_1fr_28px] gap-2 items-center">
              <Select value={String(s.month || 1)} onValueChange={(v) => { const c = [...form.schedule]; c[i] = { ...c[i], month: parseInt(v) }; setForm({ ...form, schedule: c }); }}>
                <SelectTrigger data-testid={`${testPrefix}-sch-month-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, k) => <SelectItem key={k} value={String(k + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Input inputMode="numeric" placeholder="Día" value={s.day || ""} onChange={(e) => { const c = [...form.schedule]; c[i] = { ...c[i], day: e.target.value }; setForm({ ...form, schedule: c }); }} data-testid={`${testPrefix}-sch-day-${i}`} />
              <Input inputMode="decimal" placeholder="Importe" value={s.amount} onChange={(e) => { const c = [...form.schedule]; c[i] = { ...c[i], amount: e.target.value }; setForm({ ...form, schedule: c }); }} data-testid={`${testPrefix}-sch-amount-${i}`} />
              <button type="button" onClick={() => setForm({ ...form, schedule: form.schedule.filter((_, k) => k !== i) })} className="text-muted-foreground hover:text-red-600"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <MoneyMovementFields direction="in" values={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix={testPrefix} />
      {simulateMode && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider"><FlaskConical size={13} /> Ingreso simulado</div>
          <div>
            <label className="text-xs text-muted-foreground">Expira en</label>
            <Select value={form.expires_at ? "custom" : "24"} onValueChange={(h) => {
              const t = new Date(); t.setHours(t.getHours() + parseInt(h));
              setForm({ ...form, expires_at: t.toISOString() });
            }}>
              <SelectTrigger data-testid={`${testPrefix}-expires`}><SelectValue /></SelectTrigger>
              <SelectContent>{EXP_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-[10px] text-amber-700 dark:text-amber-400">Al expirar dejará de afectar cálculos y desaparecerá automáticamente. No cuenta en Salud/Estrés/IDC.</p>
        </div>
      )}
      <Button data-testid={`${testPrefix}-submit`} onClick={onSubmit} disabled={disabled} className="w-full h-11">
        {simulateMode ? <><FlaskConical size={15} className="mr-2" /> {submitLabel} simulado</> : submitLabel}
      </Button>
    </div>
  );
}

export default function Ingresos() {
  const [items, setItems] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [audit, setAudit] = useState(null);
  const [hasPin, setHasPin] = useState(false);
  const [simCount, setSimCount] = useState(0);

  const load = () => {
    api.get("/incomes").then((r) => setItems(r.data));
    api.get("/incomes/simulated/count").then((r) => setSimCount(r.data.count));
  };
  useEffect(() => {
    load();
    api.get("/family").then((r) => setMembers(r.data.members || []));
    api.get("/auth/me").then((r) => setHasPin(!!r.data.has_pin));
  }, []);

  const buildPayload = (f, simulated) => {
    const parsedSchedule = (f.schedule || []).map((s) => ({ month: parseInt(s.month) || 1, day: parseInt(s.day) || 1, amount: parseFloat(String(s.amount).replace(",", ".")) || 0, label: s.label || "" })).filter((s) => s.amount > 0);
    const amount = f.type === "estacional" || f.type === "programado" ? parsedSchedule.reduce((s, x) => s + x.amount, 0) / Math.max(1, parsedSchedule.length) : parseFloat(String(f.amount).replace(",", ".")) || 0;
    return {
      date: f.date, concept: f.concept.trim(), amount, category: f.category,
      concept_id: f.concept_id || null,
      type: f.type, schedule: parsedSchedule, recurring: f.type === "recurrente",
      account_id: f.account_id || null, payment_method_id: f.payment_method_id || null,
      member_id: f.member_id || null,
      is_simulated: !!simulated,
      expires_at: simulated ? (f.expires_at || new Date(Date.now() + 24 * 3600 * 1000).toISOString()) : null,
    };
  };

  const submit = async (simulated) => {
    try {
      await api.post("/incomes", buildPayload(form, simulated));
      toast.success(simulated ? "Ingreso simulado añadido" : "Ingreso registrado");
      setForm({ ...EMPTY, date: todayISO() });
      setOpen(false); setSimOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/incomes/${edit.id}`, buildPayload(edit, edit.is_simulated));
      toast.success("Ingreso actualizado");
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.post(`/incomes/${del.id}/delete`, { pin: del.pin || "" });
      toast.success("Ingreso eliminado");
      setDel(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = (i) => {
    setForm({ ...EMPTY, date: todayISO(), concept: i.concept, concept_id: i.concept_id || "", member_id: i.member_id || null, amount: String(i.amount), category: i.category, type: i.type || "unico", schedule: i.schedule || [], account_id: i.account_id || "", payment_method_id: i.payment_method_id || "" });
    setOpen(true);
    toast.info("Datos copiados. Ajusta lo que necesites y guarda");
  };

  const openEdit = (i) => setEdit({ ...i, amount: String(i.amount), type: i.type || "unico", schedule: i.schedule || [], account_id: i.account_id || "", payment_method_id: i.payment_method_id || "" });

  const openAudit = async () => {
    try { const r = await api.get("/incomes/audit"); setAudit(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const total = (items || []).reduce((s, i) => s + (i.is_simulated ? 0 : i.amount), 0);

  return (
    <div className="space-y-6" data-testid="ingresos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Ingresos</h1>
          <p className="text-sm text-muted-foreground mt-1">Todos los cobros y su previsión. Los simulados no afectan a tus métricas.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="open-audit" onClick={openAudit}><ClipboardList size={14} className="mr-1" /> Auditoría</Button>
          <Button variant="outline" size="sm" data-testid="open-sim" onClick={() => { setForm({ ...EMPTY, date: todayISO() }); setSimOpen(true); }}><FlaskConical size={14} className="mr-1" /> Simular</Button>
          <Button size="sm" data-testid="open-new" onClick={() => { setForm({ ...EMPTY, date: todayISO() }); setOpen(true); }}><Plus size={14} className="mr-1" /> Nuevo</Button>
        </div>
      </div>

      {simCount > 0 && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="sim-warning">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b className="text-amber-800 dark:text-amber-300">Existen {simCount} ingreso{simCount === 1 ? "" : "s"} simulado{simCount === 1 ? "" : "s"} activo{simCount === 1 ? "" : "s"}</b> que afectan a los cálculos mostrados.
          </div>
        </div>
      )}

      <div className="border border-border rounded-lg p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Total ingresos reales</div>
        <Money value={total} testId="total-incomes" className="block mt-1 text-3xl font-semibold tabular" />
        <p className="text-xs text-muted-foreground mt-1">No incluye ingresos simulados.</p>
      </div>

      <div className="border border-border rounded-lg divide-y divide-border" data-testid="incomes-list">
        {items === null ? <div className="p-5 text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Aún no hay ingresos.</div>
          : items.map((i) => (
            <div key={i.id} className={`p-4 hover:bg-secondary/50 ${i.is_simulated ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}`} data-testid={`income-row-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{i.concept}</span>
                    <span className="text-xs bg-secondary px-1.5 py-0.5 rounded font-semibold">{i.category}</span>
                    {(i.type && i.type !== "unico") && <span className="text-[10px] uppercase tracking-wider bg-secondary/60 px-1.5 py-0.5 rounded font-bold">{i.type}</span>}
                    <MemberBadge memberId={i.member_id} />
                    {i.is_simulated && <span className="text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5"><Sparkles size={10} /> Simulado</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(i.date)}{i.expires_at ? ` · expira ${new Date(i.expires_at).toLocaleString("es-ES")}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Money value={i.amount} className="font-semibold tabular" />
                  <button data-testid={`edit-income-${i.id}`} onClick={() => openEdit(i)} className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                  <button data-testid={`duplicate-income-${i.id}`} onClick={() => duplicate(i)} className="text-muted-foreground hover:text-foreground"><Copy size={14} /></button>
                  <button data-testid={`delete-income-${i.id}`} onClick={() => setDel({ ...i, pin: "" })} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Nuevo */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Nuevo ingreso</DialogTitle></DialogHeader>
          <IncomeForm form={form} setForm={setForm} onSubmit={() => submit(false)} submitLabel="Guardar" testPrefix="new" simulateMode={false} />
        </DialogContent>
      </Dialog>

      {/* Simular */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FlaskConical size={16} /> Simulador de ingresos</DialogTitle></DialogHeader>
          <IncomeForm form={form} setForm={setForm} onSubmit={() => submit(true)} submitLabel="Simular" testPrefix="sim" simulateMode={true} />
        </DialogContent>
      </Dialog>

      {/* Editar */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Editar ingreso</DialogTitle></DialogHeader>
          {edit && <IncomeForm form={edit} setForm={setEdit} onSubmit={saveEdit} submitLabel="Guardar cambios" testPrefix="edit" simulateMode={edit.is_simulated} />}
          {edit && <MovementTrace doc={edit} />}
        </DialogContent>
      </Dialog>

      {/* Eliminar con PIN */}
      <Dialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert size={16} className="text-red-600" /> Confirmar eliminación</DialogTitle></DialogHeader>
          {del && (
            <div className="space-y-3">
              <p className="text-sm">Vas a eliminar el ingreso <b>{del.concept}</b> de <b>{fmtEUR(del.amount)}</b> del <b>{fmtDate(del.date)}</b>. Queda registrado en la auditoría.</p>
              {hasPin ? (
                <div>
                  <label className="text-xs text-muted-foreground">Introduce tu PIN secundario para confirmar</label>
                  <Input data-testid="delete-pin-input" type="password" inputMode="numeric" maxLength={4} value={del.pin} onChange={(e) => setDel({ ...del, pin: e.target.value })} autoFocus onKeyDown={(e) => e.key === "Enter" && doDelete()} />
                </div>
              ) : (
                <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded p-2">No tienes PIN secundario configurado.</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDel(null)}>Cancelar</Button>
                <Button variant="destructive" className="flex-1" data-testid="confirm-delete-btn" onClick={doDelete} disabled={hasPin && (del.pin || "").length !== 4}>Eliminar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auditoría */}
      <Dialog open={!!audit} onOpenChange={(v) => !v && setAudit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Auditoría de ingresos</DialogTitle></DialogHeader>
          {audit && audit.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Sin registros aún.</div>}
          {audit && audit.length > 0 && (
            <div className="divide-y divide-border" data-testid="audit-list">
              {audit.map((a) => (
                <div key={a.id} className="py-2 text-xs">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className={`font-bold uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded ${a.action === "delete" ? "bg-red-100 text-red-700" : a.action === "update" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{a.action}</span>
                    <span className="text-muted-foreground">{new Date(a.at).toLocaleString("es-ES")}</span>
                    <span className="text-muted-foreground">{a.user_email}</span>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    <b>{fmtEUR(a.snapshot?.amount || 0)}</b> · {a.snapshot?.concept || "—"} · {fmtDate(a.snapshot?.date)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
